import { trigger, transition, style, animate } from '@angular/animations';
import { Component, Input, QueryList, ViewChildren, inject } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SharedService } from '@shared/services/shared.service';
import { IBranchOperation } from 'app/models/licenses/branch-operation';
import { IStatus } from 'app/models/licenses/status';
import { BranchOperationService } from 'app/services/license/branch-operation.service';
import { AddressComponent } from '../address/address.component';
import { IAddress } from 'app/models/address/address';
import { ILicenseChangeDialog } from 'app/components/license/in-home-care/in-home-care-change/in-home-care-change-dialog.component';


@Component({
  selector: 'branch-operation',
  templateUrl: './branch-operation.component.html',
  styleUrls: ['./branch-operation.component.scss'],
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class BranchOperationComponent {
  @ViewChildren(AddressComponent) addresses: QueryList<AddressComponent>;
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() entityGuid: string = '';
  @Input() statuses: IStatus[] = [];
  @Input() isChange: boolean = false;
  @Input() agencyAddresses: IAddress[] = [];

  public managingOperation: boolean = false;
  public isTempOperation: boolean = false;
  public includeArchived: boolean = false;

  public operation: IBranchOperation = {
    id: 0,
    parentId: 0,
    distance: -1,
    phone: '',
    adding: false,
    removing: false,
    archived: false
  }
  public operations: IBranchOperation[] = [];
  public operationForm = new FormGroup({
    phone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)])
  });

  public sharedService = inject(SharedService);
  public operationService = inject(BranchOperationService);

  ngOnInit(): void {
    this.getOperations(this.includeArchived);
  }

  ngOnDestroy(): void {
    if(this.managingOperation){
      this.saveOperation();
    }
  }

  getOperations(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.operationService.getBranchOperations(this.entityId, this.entityType, this.includeArchived).subscribe({
      next: response => this.operations = response,
      error: e => console.log('get entity operations error', e),
      complete: () => {
        if(this.operations.length === 0 && this.isChange){
          this.createOperation();
        }
      }
    });
  }

  selectOperation(operation: IBranchOperation): void {
    this.operation = operation;
    this.managingOperation = true;
    this.updateOperationForm(operation);
  }

  toggleOperationForm(adding: boolean): void{
    this.managingOperation = adding;
    this.updateOperationForm(this.getEmptyOperation());

    if(!adding){
      if(this.isTempOperation){
        this.deleteOperation(this.operation.id);
      }
      else{
        this.operation = this.getEmptyOperation();
      }
    }
  }

  getEmptyOperation(): IBranchOperation {
    return {
      id: 0,
      parentId: 0,
      phone: '',
      distance: -1,
      adding: false,
      removing: false,
      archived: false
    }
  }

  updateOperationForm(operation: IBranchOperation): void {
    this.operationForm.reset({
      phone: operation.phone
    });
  }

  updateOperation(): void {
    let form = this.operationForm.value;
    this.operation.phone = form.phone;
  }

  createOperation(): void {
    this.operation.parentId = this.entityId;
    this.operation.adding = this.renewal;
    this.operationService.createBranchOperation(this.operation, this.entityType).subscribe({
      next: response => {
        this.operations.push(response);
        this.operation = response;
      },
      error: e => {
        this.sharedService.toastr.error("Error Creating Branch Operation");
        console.log('error creating branch operation', e);
      },
      complete: () => {
        this.isTempOperation = true;
        this.toggleOperationForm(true);
      }
    });
  }

  saveOperation(close: boolean = true): void {
    this.updateOperation();
    this.operationService.updateBranchOperation(this.operation).subscribe({
      complete: () => {
        this.isTempOperation = false;
        if(close){
          this.toggleOperationForm(false);
          if(!this.isChange){
            this.sharedService.toastr.success("Updated the operation");
          }
        }
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Save operation");
        console.log('error updating entity operation', e);
      }
    });
  }

  markForRemoval(operation: IBranchOperation, removing: boolean): void {
    operation.removing = removing;
    this.operationService.updateBranchOperation(operation).subscribe({
      complete: () => {
        let toastrMessage: string = removing? "Marked for removal" : "Marked for retention";
        this.sharedService.toastr.success(toastrMessage);
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Update Operation Removal Status");
        console.log('error updating operation removal status', e);
      }
    });
  }

  archiveOperation(operation: IBranchOperation, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this branch operation?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.operationService.archiveBranchOperation(operation.id, archive).subscribe({
            complete: () => {
              operation.archived = archive;
              if(archive){
                this.operations = this.operations.filter(o => o.id !== operation.id);
              }
              this.toggleOperationForm(false);
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Archive operation");
              console.log('error archiving branch operation', e);
            }
          });
        }
      }
    );
  }

  confirmDelete(branchId: number): void {
    this.sharedService.openConfirm('Delete this branch operation?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.deleteOperation(branchId);
        }
      }
    );
  }

  deleteOperation(branchId: number): void {
    this.operationService.deleteBranchOperation(branchId).subscribe({
      complete: () => {
        this.operations = this.operations.filter(a => a.id !== branchId);
        this.operation= this.getEmptyOperation();
      },
      error: e => console.log('error deleting operation', e)
    });
  }

  requestChange(action: string, operationId: number): void {
    let dialogData: ILicenseChangeDialog = {
      licenseType: this.sharedService.inHomeCareLicense,
      licenseId: this.entityId,
      licenseGuid: this.entityGuid,
      entityId: operationId,
      entityType: this.sharedService.branchOperation,
      originalValue: '',
      newValue: '',
      referenceAddresses: this.agencyAddresses,
      changeAction: action,
    }

    if(action === this.sharedService.change){
      let physicalAddress: IAddress = null;
      this.addresses.toArray().forEach(address => {
        let addressSet: IAddress[] = address.addresses;
        if(addressSet[0].parentId === operationId){
          physicalAddress = address.getPhysicalAddress(addressSet);
        }
      });
      dialogData.originalValue = JSON.stringify(physicalAddress);
      dialogData.newValue = this.addresses.toArray()[0].getPhysicalAddress(this.agencyAddresses);
    }
    this.sharedService.requestChange(dialogData);
  }

  getDistanceToParent(): void {
    let parentPhysicalAddress: IAddress = this.addresses.toArray()[0].getPhysicalAddress(this.agencyAddresses);
    let thisPhysicalAddress: IAddress = this.addresses.toArray()[0].getPhysicalAddress(this.addresses.toArray()[0].addresses);

    if(thisPhysicalAddress === undefined || parentPhysicalAddress === undefined){
      this.updateLicenseDistance(0, this.operation);
    }
    this.addresses.toArray()[0].getDistanceBetweenAddressess(parentPhysicalAddress, thisPhysicalAddress);
  }

  validateAddresses(): string[] {
    let errors: string[] = [];
    this.addresses.toArray().forEach(address => {
      let addressSet: IAddress[] = address.addresses;
      if(!address.getPhysicalAddress(addressSet)){
        let operation: IBranchOperation = this.operations.find(o => o.id === address.entityId);
        errors.push('Branch operation ' + operation.phone +  ' is missing a physical address');
      }
    });
    return errors;
  }

  updateLicenseDistance(distance: number, operation: IBranchOperation): void {
    this.operation = operation;
    this.operation.distance = distance;
    if(this.operation.distance > 60){
      let notice: string = `A branch operation must be fewer than 60 miles from the agency, and
        this branch appears to be more than 60 miles away. `;
      if(!this.isChange){
        notice += `You may still submit your application, but this branch
        will not be considered for the agency. For questions
        please contact the Health Care Regulation and Quality Improvement Section
        for further assistance.`;
      }
      this.sharedService.openNotice(notice)
    }
    this.saveOperation(false);
  }

  public get locked(): boolean {
    return !this.statuses.some(s => s.label === this.sharedService.started);
  }

  public get canSave(): boolean{
    let addressComplete: boolean = this.addresses[0].addresses.length > 0;
    return addressComplete && this.operationForm.valid && (!this.locked || this.isChange || this.renewal);
  }

  public get approved(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.approved);
  }

  public get renewal(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.renewal);
  }
}
